import React, { useState, useEffect } from 'react';
import { Table, Button, Input, Space, Modal, Form, Select, Tag, Avatar, Typography, message } from 'antd';
import { SearchOutlined, UserAddOutlined, EditOutlined, DeleteOutlined, UserOutlined } from '@ant-design/icons';
import { userService } from '../../services/userService';

const { Title, Text } = Typography;
const { Option } = Select;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form] = Form.useForm();

  // 获取用户列表
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await userService.getAllUsers();
      // 转换数据格式以适配表格显示
      const formattedUsers = response.map(user => ({
        id: user.id,
        email: user.email,
        username: user.username,
        role: user.role || 'user', // 默认角色
        status: user.is_verified ? 'active' : 'inactive',
        isAdmin: user.is_admin || false, // 管理员状态
        lastLogin: user.last_login ? new Date(user.last_login).toLocaleString('en-US') : 'Never logged in',
        notesCount: user.notes_count || 0,
        createdAt: new Date(user.created_at).toLocaleDateString('en-US'),
        avatar: user.picture
      }));
      setUsers(formattedUsers);
    } catch (error) {
      console.error('获取用户列表失败:', error);
      message.error('Failed to fetch user list');
    } finally {
      setLoading(false);
    }
  };

  // 组件加载时获取用户列表
  useEffect(() => {
    fetchUsers();
  }, []);

  const columns = [
    {
      title: 'User',
      dataIndex: 'username',
      key: 'username',
      render: (text, record) => (
        <Space>
          <Avatar 
            icon={<UserOutlined />} 
            src={record.avatar} 
            style={{ backgroundColor: record.avatar ? 'transparent' : '#1890ff' }}
          />
          <div>
            <div>{text}</div>
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.email}</Text>
          </div>
        </Space>
      ),
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value, record) =>
        record.username.toLowerCase().includes(value.toLowerCase()) ||
        record.email.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => (
        <Tag color={role === 'admin' ? 'red' : 'blue'}>
          {role === 'admin' ? 'Admin' : 'Regular User'}
        </Tag>
      ),
      filters: [
        { text: 'Admin', value: 'admin' },
        { text: 'Regular User', value: 'user' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Status (is_verified)',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status === 'active' ? 'Active' : 'Inactive'}
        </Tag>
      ),
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Admin',
      dataIndex: 'isAdmin',
      key: 'isAdmin',
      render: (isAdmin) => (
        <Tag color={isAdmin ? 'red' : 'default'}>
          {isAdmin ? 'Yes' : 'No'}
        </Tag>
      ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
      ],
      onFilter: (value, record) => record.isAdmin === value,
    },
    {
      title: 'Notes Count',
      dataIndex: 'notesCount',
      key: 'notesCount',
      sorter: (a, b) => a.notesCount - b.notesCount,
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      sorter: (a, b) => new Date(a.lastLogin) - new Date(b.lastLogin),
    },
    {
      title: 'Registered At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: 'Actions',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const handleAdd = () => {
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      role: user.role,
      status: user.status,
      isAdmin: user.isAdmin // 设置管理员权限
    });
    setIsModalVisible(true);
  };

  const handleDelete = (user) => {
    Modal.confirm({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete user "${user.username}"? This action cannot be undone.`,
      okText: 'Confirm',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await userService.deleteUser(user.id);
          message.success('User deleted successfully');
          fetchUsers(); // 重新获取用户列表
        } catch (error) {
          console.error('删除用户失败:', error);
          message.error('Failed to delete user');
        }
      },
    });
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      if (editingUser) {
        // 编辑用户
        await userService.updateUser(editingUser.id, {
          username: values.username,
          email: values.email,
          role: values.role,
          is_verified: values.status === 'active',
          is_admin: values.isAdmin // 更新管理员权限
        });
        message.success('User updated successfully');
      } else {
        // 添加用户
        await userService.createUser({
          username: values.username,
          email: values.email,
          role: values.role,
          is_verified: values.status === 'active',
          is_admin: values.isAdmin, // 添加管理员权限
          password: 'defaultPassword123' // 临时密码，实际应用中应该让用户设置
        });
        message.success('User added successfully');
      }
      
      setIsModalVisible(false);
      fetchUsers(); // 重新获取用户列表
    } catch (error) {
      console.error('保存用户失败:', error);
      message.error('Failed to save user');
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <div className="admin-module">
      <div className="admin-module-header">
        <Title level={3} className="admin-module-title">User Management</Title>
        <Text className="admin-module-description">
          Manage system users, including adding, editing, and deleting user information
        </Text>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Space>
          <Input.Search
            placeholder="Search username or email"
            allowClear
            onSearch={handleSearch}
            style={{ width: 300 }}
          />
          <Button
            type="primary"
            icon={<UserAddOutlined />}
            onClick={handleAdd}
          >
            Add User
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={users}
        loading={loading}
        rowKey="id"
        pagination={{
          total: users.length,
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} records`,
        }}
      />

      <Modal
        title={editingUser ? 'Edit User' : 'Add User'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="Confirm"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            role: 'user',
            status: 'active',
            isAdmin: false, // 初始化管理员权限为否
          }}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Please enter username!' }]}
          >
            <Input placeholder="Enter username" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email!' },
              { type: 'email', message: 'Please enter a valid email address!' }
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>
          <Form.Item
            name="role"
            label="Role"
            rules={[{ required: true, message: 'Please select a role!' }]}
          >
            <Select placeholder="Select role">
              <Option value="user">Regular User</Option>
              <Option value="admin">Admin</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: 'Please select status!' }]}
          >
            <Select placeholder="Select status">
              <Option value="active">Active</Option>
              <Option value="inactive">Inactive</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="isAdmin"
            label="Admin Permission"
            valuePropName="checked"
          >
            <Select placeholder="Select admin permission">
              <Option value={true}>Yes</Option>
              <Option value={false}>No</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagement; 